import { useCallback, useEffect, useRef, useState } from "react"

import type { MetricRecordData } from "@/types/metrics/contracts"

export function useRegexSaveValidation(metric: MetricRecordData) {
  const currentMetric = useRef<MetricRecordData | null>(metric)
  const [rejection, setRejection] = useState<{
    configuration: string
    message: string
  } | null>(null)
  const configuration = JSON.stringify([
    metric.id,
    metric.metric_manager,
    metric.metric_metadata?.regex_pattern,
    metric.metric_metadata?.case_insensitive,
  ])

  useEffect(() => {
    currentMetric.current = metric
    return () => {
      currentMetric.current = null
    }
  }, [metric])

  // Reject stale responses after edits, resource changes, or unmount so callers show their failure toast;
  // the configuration key also hides rejections that race a render before the ref's effect runs.
  const recordRegexError = useCallback(
    (error: unknown): boolean => {
      if (!error || typeof error !== "object" || !("detail" in error)) {
        return false
      }
      const detail = error.detail
      if (
        !detail ||
        typeof detail !== "object" ||
        !("code" in detail) ||
        detail.code !== "invalid_regex_pattern" ||
        !("message" in detail) ||
        typeof detail.message !== "string"
      ) {
        return false
      }
      if (currentMetric.current !== metric) {
        return false
      }
      setRejection({ configuration, message: detail.message })
      return true
    },
    [configuration, metric],
  )

  return {
    regexValidationError:
      rejection?.configuration === configuration ? rejection.message : null,
    recordRegexError,
  }
}
